import type {Nullable} from '#types';

/**
 * Takes a string, returns an unsigned 32-bit number
 */
export function hashString(str: string) {
  let h = 0;

  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }

  return Math.abs(h);
}

type TransformRule = [string | RegExp, string];

/**
 * Transform a string to a consistent format
 * @param str - The string to transform
 * @param rules - The rules to apply to the string
 * @returns The transformed string
 */
export const transformString = (
  str: string,
  rules: Array<TransformRule>
): string => {
  return rules.reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    str
  );
};

/**
 * Fuzzy match two strings
 * @param a - The first string
 * @param b - The second string
 * @returns True if the strings are fuzzy matches
 */
export const fuzzyMatch = (
  a: string,
  b: string,
  customRules?: Array<TransformRule>
): boolean => {
  const rules: Array<TransformRule> = [[/[\s_-]/g, ''], ...(customRules ?? [])];

  const lowerA = a.toLowerCase();
  const lowerB = b.toLowerCase();

  return (
    transformString(lowerA, rules).includes(transformString(lowerB, rules)) ||
    transformString(lowerB, rules).includes(transformString(lowerA, rules))
  );
};

/** Determines if a value is a nonempty string or not */
export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Truncate a string with ellipses
 * @param text - The text to truncate
 * @param maxLength - The maximum length of the text
 * @returns The truncated text
 */
export const truncateWithEllipses = (
  text: string,
  maxLength: number
): string => {
  if (text.length <= maxLength) return text;

  return `${text.substring(0, maxLength - 1)}...`;
};

export function safeJsonParse<T>(
  jsonString: Nullable<string>,
  fallback: T,
  {warnOnError = true}: {warnOnError?: boolean} = {}
): T {
  if (!jsonString) return fallback;

  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    if (warnOnError) console.warn('Failed to parse JSON props:', error);
    return fallback;
  }
}

const DEFAULT_LOCALE = 'en';
const defaultPluralRules = new Intl.PluralRules(DEFAULT_LOCALE, {
  type: 'cardinal',
});

/**
 * Returns a full phrase with quantity and singular/plural word (e.g. "3 datasets", "0 errors", "1 segment").
 * Use for UI labels and messages that include the number.
 * @param quantity - The numeric quantity (formatted with toLocaleString)
 * @param singularForm - The singular form of the word
 * @param extra - Optional plural form, form for zero, and/or locale (e.g. { pluralForm: 'tables', zeroForm: 'tables', locale: 'en' })
 * @returns The full phrase (e.g. "3 datasets")
 */
export function pluralize(
  quantity: number,
  singularForm: string,
  extra?: {
    pluralForm?: string;
    zeroForm?: string;
    locale?: string;
  }
): string {
  const plural = extra?.pluralForm ?? `${singularForm}s`;
  const pr =
    extra?.locale !== undefined
      ? new Intl.PluralRules(extra.locale, {type: 'cardinal'})
      : defaultPluralRules;
  const category = pr.select(quantity);

  let word: string;
  if (quantity === 0) {
    word = extra?.zeroForm ?? plural;
  } else if (category === 'one') {
    word = singularForm;
  } else {
    word = plural;
  }

  if (quantity === 0) {
    return `0 ${word}`;
  }
  const formatted = quantity.toLocaleString(extra?.locale ?? DEFAULT_LOCALE);
  return `${formatted} ${word}`;
}

/**
 * Returns the singular or plural form of a word based on the quantity.
 * Use when you need the word form that matches a quantity (e.g. "day" for 1, "days" for 5).
 * @param quantity - The numeric quantity to determine singular vs plural
 * @param singularForm - The singular form of the word
 * @param extra - Optional plural form and/or locale (e.g. { pluralForm: 'Opportunities', locale: 'en' })
 * @returns The appropriate singular or plural form
 */
export function getPluralizedWord(
  quantity: number,
  singularForm: string,
  extra?: {
    pluralForm?: string;
    locale?: string;
  }
): string {
  const pr =
    extra?.locale !== undefined
      ? new Intl.PluralRules(extra.locale, {type: 'cardinal'})
      : defaultPluralRules;
  const category = pr.select(quantity);
  if (category === 'one') {
    return singularForm;
  }
  if (extra?.pluralForm !== undefined) {
    return extra.pluralForm;
  }
  if (
    pr.resolvedOptions().locale.startsWith('en') &&
    /[^aeiou]y$/i.test(singularForm)
  ) {
    return singularForm.replace(/y$/i, ending =>
      ending === 'Y' ? 'IES' : 'ies'
    );
  }
  return `${singularForm}s`;
}

export const toPascalCase = (str: string) =>
  str
    .split('_')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');

const VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export function isValidEmailAddress(value: string): boolean {
  return VALID_EMAIL_REGEX.test(value);
}
